import { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useFavoriteProducts } from '../../../hooks/useFavoriteProducts';
import { ProductPrice, useProductPricesSub } from '../../../hooks/useProductPricesSub';
import { ProductType, useProducts } from '../../../hooks/useProducts';
import { useTailwindBreakpoint } from '../../../hooks/useTailwindBreakpoint';
import { cn } from '../../../lib/classNameHelpers';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { Card } from '../../Card';
import { AutoScroll } from './AutoScroll';
import { ScrollableSection } from './ScrollableSection';
import { ScrollableSectionLoader } from './ScrollableSectionLoader';

export type ProductWithPrice = ProductType & Pick<ProductPrice, 'midPrice' | 'change24hrPercent'>;

type TradeFavoritesUiProps = {
  hotMarkets: ProductWithPrice[];
  favorites: ProductWithPrice[];
  isLoading?: boolean;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
};

export function TradeFavoritesUi({
  hotMarkets,
  favorites,
  isLoading = false,
  isPlaying,
  setIsPlaying,
}: TradeFavoritesUiProps) {
  if (isLoading) {
    return (
      <Card className="w-full p-1">
        <div className="flex gap-2">
          <ScrollableSectionLoader label="Favorites" />
          <ScrollableSectionLoader label="Hot" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full px-2">
      <div className="flex gap-2">
        {favorites.length > 0 && (
          <ScrollableSection
            label="Favorites"
            data={favorites}
            className={cn('max-w-[70%]', favorites.length > 5 ? 'flex-2' : 'flex-none')}
          />
        )}
        <AutoScroll
          label="Hot"
          className="flex-1"
          scrollSpeed={15}
          data={hotMarkets}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
        />
      </div>
    </Card>
  );
}

export function TradeFavorites() {
  const { data: products = [], isLoading } = useProducts();
  const { favoriteProducts } = useFavoriteProducts();
  const { data: pricesByProductId, isPending } = useProductPricesSub();
  const [isPaused, setIsPaused] = useLocalStorage(localStorageKeys.hotBarAutoScrollPaused, false);

  const is2xl = useTailwindBreakpoint('2xl');
  const is3xl = useTailwindBreakpoint('3xl');
  const is4xl = useTailwindBreakpoint('4xl');
  const is5xl = useTailwindBreakpoint('5xl');

  const isLoadingPrices = Object.keys(pricesByProductId).length <= 0;

  const hotMarketsCount = useMemo(() => {
    if (is5xl) return 16;
    if (is4xl) return 14;
    if (is3xl) return 12;
    if (is2xl) return 10;
    return 8;
  }, [is2xl, is3xl, is4xl, is5xl]);

  const favorites = useMemo(() => {
    if (isLoadingPrices) {
      return [];
    }

    const favoriteIds = Object.keys(favoriteProducts);

    return products.reduce<ProductWithPrice[]>((acc, product) => {
      if (favoriteIds.includes(product.id)) {
        const price = pricesByProductId[product.id];

        acc.push({
          ...product,
          midPrice: price?.midPrice,
          change24hrPercent: price?.change24hrPercent,
        });
      }

      return acc;
    }, []);
  }, [favoriteProducts, isLoadingPrices, pricesByProductId, products]);

  const hotMarkets = useMemo(() => {
    if (isLoadingPrices) {
      return [];
    }

    const productsWithPrice: ProductWithPrice[] = [];

    for (const product of products) {
      const price = pricesByProductId[product.id];

      if (price) {
        productsWithPrice.push({
          ...product,
          midPrice: price.midPrice,
          change24hrPercent: price.change24hrPercent,
        });
      }
    }

    const sortedProducts = productsWithPrice.sort((a, b) => {
      const aChange = a.change24hrPercent || 0n;
      const bChange = b.change24hrPercent || 0n;
      return bChange > aChange ? 1 : bChange < aChange ? -1 : 0;
    });

    return sortedProducts.slice(0, hotMarketsCount);
  }, [isLoadingPrices, pricesByProductId, products, hotMarketsCount]);

  return (
    <TradeFavoritesUi
      favorites={favorites}
      hotMarkets={hotMarkets}
      isLoading={isLoading || isLoadingPrices || isPending}
      isPlaying={!isPaused}
      setIsPlaying={(isPlaying) => setIsPaused(!isPlaying)}
    />
  );
}
